import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import { type Dv01ValidationFailRatesRow, type Dv01ValidationPassRatesRow } from './utils/dv01ValidationRowTypes';

/** Matches API `?source=` for Data Tape Validation Summary (All / DV01 / Setpoint). */
export type DataValidationKpiSource = 'all' | 'dv01' | 'setpoint';

export const DATA_VALIDATION_KPI_SOURCE_DEFAULT: DataValidationKpiSource = 'dv01';

/**
 * Display-only UPPERCASE for facility / deal names (matches aga-dashboard).
 * Collapses whitespace; does not alter API identity keys.
 */
function formatFacilityDisplayName(value: string | null | undefined): string {
  if (value == null) {
    return '';
  }
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed) {
    return '';
  }
  return trimmed.toUpperCase();
}

export type DataValidationSummaryKpi = {
  avgPassRatePctAllDeals: number | null;
  /** Distinct deals (facilities) present in the pass-rates view. */
  distinctDeals: number;
  totalFailingLoans: number;
  totalPassingLoans: number;
  totalRules: number;
  totalUniqueRules: number;
  /** Distinct deals (facilities) involved in failures. */
  distinctFailingDeals: number;
};

export type DataValidationSummaryDashboard = {
  kpi: DataValidationSummaryKpi | null;
  failTableRows: Dv01ValidationFailRatesRow[];
  passTableRows: Dv01ValidationPassRatesRow[];
};

const DATA_VALIDATION_SUMMARY_DASHBOARD_PATH = 'data-validation-summary/dashboard';
const DATA_VALIDATION_SUMMARY_KPI_PATH = 'data-validation-summary/kpi';
const DATA_VALIDATION_DATA_TAPE_TRACKER_PATH = 'data-validation-summary/data-tape-tracker';
const DATA_VALIDATION_RULES_SUMMARY_PATH = 'data-validation-summary/rules-summary';
const DATA_VALIDATION_FAILS_OVER_TIME_PATH = 'data-validation-summary/fails-over-time';
const DATA_VALIDATION_TOP_FAILING_DEALS_PATH = 'data-validation-summary/top-failing-deals';
const DATA_VALIDATION_FACILITY_FAILURES_PATH = 'data-validation-summary/facility-failures-over-time';
const DATA_VALIDATION_RULE_FAILURE_BREAKDOWN_PATH = 'data-validation-summary/rule-failure-breakdown';
const DATA_VALIDATION_ACTIVE_FAILING_DEALS_PATH = 'data-validation-summary/active-failing-deals';
const DATA_VALIDATION_FAILS_BY_DATE_PATH = 'data-validation-summary/fails-by-date';
const LOAN_LEVEL_FAILURES_AS_OF_DATES_PATH = 'DV01/LoanLevelFailures/as-of-dates';

export type DataValidationRulesSummaryRow = {
  riskFlag: string | null;
  columnName: string | null;
  checkName: string | null;
  /** dv01 or setpoint — present when source=all so overlapping catalogs stay unique. */
  source?: string | null;
};

export type DataValidationTapeTrackerCell = {
  riskFlag: string;
  dealName: string;
  facilityId: string;
  /** Month bucket as YYYY-MM (sortable as string). */
  asOfMonth: string;
  /** Human label, e.g. "Aug 24". */
  monthLabel: string;
  /** 1 if the deal has tape rows that month, 0 otherwise. */
  hasData: number;
  sampleAsOfDate: string | null;
  /** True when CLS reports no active facility status for this IQEQ × month. */
  isTerminated?: boolean;
};

/**
 * One tape-date roll-up row for the Summary of Fails by Date table. The loan-level drill-down for
 * each date is loaded on demand via SSRM, so no facility detail is embedded here.
 */
export type DataValidationFailsByDateRow = {
  /** Tape date as YYYY-MM-DD. */
  asOfDate: string;
  totalFailingLoans: number;
  distinctRulesTriggered: number;
};

export type DataValidationFailsOverTimePoint = {
  /** Month bucket as YYYY-MM. */
  asOfMonth: string;
  /** Human label, e.g. "Aug 24". */
  monthLabel: string;
  failingLoans: number;
  /** Distinct loans across the aggregated tapes for the month (denominator). */
  totalLoans: number;
  /** Portfolio fail rate proportion 0–1 from API. */
  failurePct: number | null;
};

export type DataValidationRuleChangeType = 'first_appearance' | 'rule_escalation' | 'rule_reduction' | 'no_change';

/** One facility × period row for the Top 5 Failing Deals chart and stat cards. */
export type DataValidationTopFailingDealPoint = {
  facilityRank: number;
  facilityId: number | null;
  facilityName: string | null;
  totalFailingLoans: number;
  /** Period date as YYYY-MM-DD. */
  asOfDate: string;
  /** Human label, e.g. "May-26". */
  monthLabel: string;
  /** Distinct failing loans (null when the facility was inactive that period). */
  failingLoans: number | null;
  /** Total failing loans across all deals that period (denominator for "% of total"). */
  periodTotalFailingLoans: number | null;
  failingRules: number | null;
  failureRows: number | null;
  columnsInvolved: string | null;
  rulesInvolved: string | null;
  ruleChangeType: DataValidationRuleChangeType;
  momFailingLoansDelta: number | null;
  peakFailingLoans: number | null;
  activePeriods: number;
};

async function parseJsonResponse<T>(raw: unknown): Promise<T | null> {
  if (typeof Response !== 'undefined' && raw instanceof Response) {
    if (!raw.ok) {
      throw new ResponseError(`HTTP ${raw.status}`, raw.status);
    }
    const text = await raw.text();
    if (!text.trim()) {
      return null;
    }
    return JSON.parse(text) as T;
  }

  if (raw === null || raw === undefined) {
    return null;
  }
  return raw as T;
}

/** Chart rows for the Fails over Time combo chart (bars + fail % line). */
export type DataValidationFailsOverTimeChartRow = {
  asOfMonth: string;
  monthLabel: string;
  failingLoans: number;
  /** Passing loans for the month (total loans − failing loans). */
  passingLoans: number;
  /** Fail rate as percent 0–100 for chart display (API ratio × 100). */
  failurePct: number | null;
  /** Pass rate as percent 0–100 for chart display ((1 − API ratio) × 100). */
  passRatePct: number | null;
};

function formatMonYyLabel(asOfMonth: string, monthLabel?: string | null): string {
  if (monthLabel?.includes('-')) {
    return monthLabel;
  }
  const parsed = /^(\d{4})-(\d{2})/.exec(asOfMonth);
  if (parsed) {
    const year = Number(parsed[1]);
    const month = Number(parsed[2]);
    const mon = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short' });
    return `${mon}-${String(year).slice(-2)}`;
  }
  if (monthLabel) {
    return monthLabel.replace(' ', '-');
  }
  return asOfMonth;
}

export function dataValidationFailsOverTimeRowsToChartRows(
  rows: readonly DataValidationFailsOverTimePoint[],
): DataValidationFailsOverTimeChartRow[] {
  return [...rows]
    .filter(row => row.asOfMonth)
    .sort((a, b) => a.asOfMonth.localeCompare(b.asOfMonth))
    .map(row => ({
      asOfMonth: row.asOfMonth,
      monthLabel: formatMonYyLabel(row.asOfMonth, row.monthLabel),
      failingLoans: row.failingLoans,
      passingLoans: Math.max(0, (row.totalLoans ?? 0) - row.failingLoans),
      failurePct: row.failurePct != null ? row.failurePct * 100 : null,
      passRatePct: row.failurePct != null ? (1 - row.failurePct) * 100 : null,
    }));
}

/** One facility line for the Top 5 Failing Deals chart. */
export type DataValidationTopFailingDealSeries = {
  /** Stable per-facility key used as the AG Charts yKey. */
  key: string;
  facilityId: number | null;
  facilityName: string;
  facilityRank: number;
};

/** A single stat card under the Top 5 Failing Deals chart. */
export type DataValidationTopFailingDealStatCard = {
  key: string;
  facilityName: string;
  facilityRank: number;
  /** Most recent non-null failing loans. */
  latestFailingLoans: number | null;
  /** Month-over-month delta at the latest active period. */
  latestMomDelta: number | null;
  peakFailingLoans: number | null;
  activePeriods: number;
};

/** Pivoted chart row: one period with a failing-loan value (and meta) per facility series. */
export type DataValidationTopFailingDealsChartRow = {
  asOfDate: string;
  monthLabel: string;
  [seriesValue: string]: string | number | null | undefined;
};

export type DataValidationTopFailingDealsView = {
  series: DataValidationTopFailingDealSeries[];
  chartRows: DataValidationTopFailingDealsChartRow[];
  statCards: DataValidationTopFailingDealStatCard[];
};

/** Suffix helpers so meta fields never collide with the numeric yKey. */
export const TOP_DEAL_RULE_CHANGE_SUFFIX = '__rc';
export const TOP_DEAL_RULE_COUNT_SUFFIX = '__rules';
/** Raw failing-loan count stored alongside the %-of-total yKey value, for tooltips. */
export const TOP_DEAL_LOANS_SUFFIX = '__loans';

function topDealSeriesKey(point: DataValidationTopFailingDealPoint): string {
  return point.facilityId != null
    ? `f_${point.facilityId}`
    : `r_${point.facilityRank}_${point.facilityName ?? 'unknown'}`;
}

export function dataValidationTopFailingDealsToView(
  rows: readonly DataValidationTopFailingDealPoint[],
): DataValidationTopFailingDealsView {
  if (rows.length === 0) {
    return { series: [], chartRows: [], statCards: [] };
  }

  const seriesByKey = new Map<string, DataValidationTopFailingDealSeries>();
  for (const row of rows) {
    const key = topDealSeriesKey(row);
    if (!seriesByKey.has(key)) {
      seriesByKey.set(key, {
        key,
        facilityId: row.facilityId,
        facilityName: formatFacilityDisplayName(row.facilityName ?? `Facility ${row.facilityId ?? '—'}`),
        facilityRank: row.facilityRank,
      });
    }
  }
  const series = [...seriesByKey.values()].sort((a, b) => a.facilityRank - b.facilityRank);

  const periodMonthLabel = new Map<string, string>();
  for (const row of rows) {
    if (row.asOfDate && !periodMonthLabel.has(row.asOfDate)) {
      periodMonthLabel.set(row.asOfDate, formatMonYyLabel(row.asOfDate, row.monthLabel));
    }
  }
  const periods = [...periodMonthLabel.keys()].sort((a, b) => a.localeCompare(b));

  const rowByPeriod = new Map<string, DataValidationTopFailingDealsChartRow>();
  for (const period of periods) {
    rowByPeriod.set(period, {
      asOfDate: period,
      monthLabel: periodMonthLabel.get(period) ?? period,
    });
  }

  for (const point of rows) {
    const chartRow = rowByPeriod.get(point.asOfDate);
    if (!chartRow) {
      continue;
    }
    const key = topDealSeriesKey(point);
    const loans = point.failingLoans;
    const periodTotal = point.periodTotalFailingLoans;
    const pct = loans != null && periodTotal != null && periodTotal > 0 ? (loans / periodTotal) * 100 : undefined;
    chartRow[key] = pct;
    chartRow[`${key}${TOP_DEAL_LOANS_SUFFIX}`] = loans ?? undefined;
    chartRow[`${key}${TOP_DEAL_RULE_CHANGE_SUFFIX}`] = point.ruleChangeType;
    chartRow[`${key}${TOP_DEAL_RULE_COUNT_SUFFIX}`] = point.failingRules;
  }

  const chartRows = periods.map(period => rowByPeriod.get(period)!);

  const statCards: DataValidationTopFailingDealStatCard[] = series.map(s => {
    const facilityRows = rows
      .filter(row => topDealSeriesKey(row) === s.key)
      .sort((a, b) => a.asOfDate.localeCompare(b.asOfDate));

    let latestFailingLoans: number | null = null;
    let latestMomDelta: number | null = null;
    let peakFailingLoans: number | null = null;
    let activePeriods = 0;

    for (const row of facilityRows) {
      if (row.peakFailingLoans != null) {
        peakFailingLoans =
          peakFailingLoans == null ? row.peakFailingLoans : Math.max(peakFailingLoans, row.peakFailingLoans);
      }
      if (row.activePeriods != null) {
        activePeriods = Math.max(activePeriods, row.activePeriods);
      }
      if (row.failingLoans != null) {
        latestFailingLoans = row.failingLoans;
        latestMomDelta = row.momFailingLoansDelta ?? null;
      }
    }

    return {
      key: s.key,
      facilityName: s.facilityName,
      facilityRank: s.facilityRank,
      latestFailingLoans,
      latestMomDelta,
      peakFailingLoans,
      activePeriods,
    };
  });

  return { series, chartRows, statCards };
}

/** One facility × period row for the Single-facility deep dive section. */
export type DataValidationFacilityFailuresPoint = {
  facilityId: number | null;
  facilityName: string | null;
  /** Period date as YYYY-MM-DD. */
  asOfDate: string;
  /** Human label, e.g. "May-26". */
  monthLabel: string;
  failingLoans: number;
  failingRules: number;
  failureRows: number;
  /** Total loans for the facility in the period (from the aggregated tapes); null when unavailable. */
  totalLoans: number | null;
  /** dv01 or setpoint — used for All-mode dropdown identity. */
  source?: DataValidationKpiSource | 'dv01' | 'setpoint' | null;
};

/** One selectable facility for the deep-dive dropdown. */
export type DataValidationFacilityOption = {
  key: string;
  facilityId: number | null;
  facilityName: string;
  source?: DataValidationKpiSource | 'dv01' | 'setpoint' | null;
};

/** One chart point on the shared period axis (failing loans null when inactive). */
export type DataValidationFacilityChartRow = {
  asOfDate: string;
  monthLabel: string;
  failingLoans: number | null;
  failingRules: number | null;
  /**
   * Deal's failing loans as a % of ALL deals' failing loans that period (0–100).
   * Mirrors the Top 5 chart's "% of period fails" logic, applied deal by deal.
   */
  failPct: number | null;
  /** Pass rate = 100 − failPct (null when period total unavailable). */
  passPct: number | null;
};

/** Footer stats for the deep-dive chart. */
export type DataValidationFacilityStats = {
  firstPeriod: string | null;
  lastPeriod: string | null;
  periods: number;
  peakFailingLoans: number | null;
};

export type DataValidationFacilityFailuresView = {
  facilities: DataValidationFacilityOption[];
  /** All distinct periods across every facility (sorted ascending) for a stable x-axis. */
  periods: { asOfDate: string; monthLabel: string }[];
  /** Rows grouped by facility key. */
  rowsByFacility: Record<string, DataValidationFacilityFailuresPoint[]>;
  /**
   * Total failing loans across ALL deals per period (asOfDate → sum of failing loans).
   * Same denominator the Top 5 chart uses for "% of period fails".
   */
  periodFailTotals: Record<string, number>;
};

function facilityOptionKey(point: DataValidationFacilityFailuresPoint): string {
  const src = point.source === 'setpoint' ? 'setpoint' : 'dv01';
  if (src === 'setpoint') {
    return `setpoint:n_${point.facilityName ?? 'unknown'}`;
  }
  return point.facilityId != null ? `dv01:f_${point.facilityId}` : `dv01:n_${point.facilityName ?? 'unknown'}`;
}

function facilityOptionLabel(point: DataValidationFacilityFailuresPoint, disambiguateSources: boolean): string {
  const base = formatFacilityDisplayName(point.facilityName ?? `Facility ${point.facilityId ?? '—'}`);
  if (!disambiguateSources) {
    return base;
  }
  const tag = point.source === 'setpoint' ? 'Setpoint' : 'DV01';
  return `${base} (${tag})`;
}

export function dataValidationFacilityFailuresToView(
  rows: readonly DataValidationFacilityFailuresPoint[],
): DataValidationFacilityFailuresView {
  const facilityByKey = new Map<string, DataValidationFacilityOption>();
  const rowsByFacility: Record<string, DataValidationFacilityFailuresPoint[]> = {};
  const periodMonthLabel = new Map<string, string>();
  const periodFailTotals: Record<string, number> = {};
  const hasDv01 = rows.some(row => row.source !== 'setpoint');
  const hasSetpoint = rows.some(row => row.source === 'setpoint');
  const disambiguateSources = hasDv01 && hasSetpoint;

  for (const row of rows) {
    const key = facilityOptionKey(row);
    if (!facilityByKey.has(key)) {
      facilityByKey.set(key, {
        key,
        facilityId: row.facilityId,
        facilityName: facilityOptionLabel(row, disambiguateSources),
        source: row.source === 'setpoint' ? 'setpoint' : 'dv01',
      });
    }
    (rowsByFacility[key] ??= []).push(row);
    if (row.asOfDate && !periodMonthLabel.has(row.asOfDate)) {
      periodMonthLabel.set(row.asOfDate, formatMonYyLabel(row.asOfDate, row.monthLabel));
    }
    // Sum failing loans across every deal per period — the Top 5 "% of period fails" denominator.
    periodFailTotals[row.asOfDate] = (periodFailTotals[row.asOfDate] ?? 0) + (row.failingLoans ?? 0);
  }

  const facilities = [...facilityByKey.values()].sort((a, b) => a.facilityName.localeCompare(b.facilityName));

  const periods = [...periodMonthLabel.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map(asOfDate => ({ asOfDate, monthLabel: periodMonthLabel.get(asOfDate) ?? asOfDate }));

  return { facilities, periods, rowsByFacility, periodFailTotals };
}

export function buildFacilityChartRows(
  view: DataValidationFacilityFailuresView,
  facilityKey: string,
): DataValidationFacilityChartRow[] {
  const facilityRows = view.rowsByFacility[facilityKey] ?? [];
  const byPeriod = new Map(facilityRows.map(row => [row.asOfDate, row]));

  return view.periods.map(period => {
    const match = byPeriod.get(period.asOfDate);
    const failing = match ? match.failingLoans : null;
    // Same logic as the Top 5 chart, deal by deal: this deal's failing loans as a
    // % of ALL deals' failing loans that period. Pass % is then 100 − failure %.
    const periodTotal = view.periodFailTotals[period.asOfDate] ?? 0;
    const failPct = failing != null && periodTotal > 0 ? (failing / periodTotal) * 100 : null;
    const passPct = failPct != null ? 100 - failPct : null;
    return {
      asOfDate: period.asOfDate,
      monthLabel: period.monthLabel,
      failingLoans: failing,
      failingRules: match ? match.failingRules : null,
      failPct,
      passPct,
    };
  });
}

export function buildFacilityStats(
  view: DataValidationFacilityFailuresView,
  facilityKey: string,
): DataValidationFacilityStats {
  const facilityRows = [...(view.rowsByFacility[facilityKey] ?? [])].sort((a, b) =>
    a.asOfDate.localeCompare(b.asOfDate),
  );
  if (facilityRows.length === 0) {
    return { firstPeriod: null, lastPeriod: null, periods: 0, peakFailingLoans: null };
  }
  return {
    firstPeriod: facilityRows[0]!.asOfDate,
    lastPeriod: facilityRows[facilityRows.length - 1]!.asOfDate,
    periods: facilityRows.length,
    peakFailingLoans: Math.max(...facilityRows.map(row => row.failingLoans)),
  };
}

/** One asset-class × column × rule row for the failing-loans concentration table. */
export type DataValidationRuleFailureBreakdownRow = {
  assetClass: string | null;
  columnRuleRunOn: string | null;
  validationRule: string | null;
  failingLoans: number;
  facilitiesInvolved: string | null;
  /** dv01 or setpoint — used for All-mode row identity. */
  source?: DataValidationKpiSource | 'dv01' | 'setpoint';
};

/** One monthly point for the "Active failing deals & rule volume" chart. */
export type DataValidationActiveFailingDealsPoint = {
  /** Period (month start) as YYYY-MM-DD. */
  period: string;
  /** Human label, e.g. "Jul-24". */
  monthLabel: string;
  facilitiesFailing: number;
  totalRuleInstances: number;
  totalFailingLoans: number;
  /** "name — N loans / M rules" entries joined by " | ". */
  facilityDetail: string | null;
  facilityNames: string | null;
  topFacility: string | null;
  topFacilityLoans: number;
};

/** One x-axis point for the active-failing-deals chart. */
export type DataValidationActiveFailingDealsChartRow = {
  monthLabel: string;
  facilitiesFailing: number;
  totalRuleInstances: number;
  totalFailingLoans: number;
  /** Facility lines (split out of the LISTAGG detail) for the tooltip. */
  facilityLines: string[];
};

export function dataValidationActiveFailingDealsToChartRows(
  rows: readonly DataValidationActiveFailingDealsPoint[],
): DataValidationActiveFailingDealsChartRow[] {
  return [...rows]
    .sort((a, b) => a.period.localeCompare(b.period))
    .map(row => ({
      monthLabel: formatMonYyLabel(row.period, row.monthLabel),
      facilitiesFailing: row.facilitiesFailing,
      totalRuleInstances: row.totalRuleInstances,
      totalFailingLoans: row.totalFailingLoans,
      facilityLines: (row.facilityDetail ?? '')
        .split(' | ')
        .map(part => part.trim())
        .filter(Boolean),
    }));
}

/** Title-case an underscore asset-class slug, e.g. "energy_residential" → "Energy Residential". */
export function prettifyAssetClass(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .split(',')
    .map(slug =>
      slug
        .trim()
        .split('_')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '),
    )
    .filter(Boolean)
    .join(', ');
}

export function formatPassRatePct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return `${value.toFixed(1)}%`;
}

export function formatLoanCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return Math.round(value).toLocaleString('en-US');
}

/**
 * Compact loan-count display: values of a billion or more render as "#.##B", a million or more as
 * "#.##M", and anything smaller keeps full comma-grouped digits. Pair with `formatLoanCount` (e.g.
 * as a hover title) when the exact value should stay available.
 */
export function formatLoanCountCompact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (magnitude >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2)}M`;
  }
  return Math.round(value).toLocaleString('en-US');
}

export class DataValidationSummaryService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  fetchDataValidationSummaryDashboard = async (
    source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT,
  ) => {
    const path = `${DATA_VALIDATION_SUMMARY_DASHBOARD_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    return parseJsonResponse<DataValidationSummaryDashboard>(raw);
  };

  fetchDataValidationDataTapeTracker = async () => {
    const raw = await this.get<unknown>({ path: DATA_VALIDATION_DATA_TAPE_TRACKER_PATH } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationTapeTrackerCell[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationTapeTrackerCell[]) : [];
  };

  fetchDataValidationRulesSummary = async (source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT) => {
    const path = `${DATA_VALIDATION_RULES_SUMMARY_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationRulesSummaryRow[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationRulesSummaryRow[]) : [];
  };

  fetchDataValidationFailsOverTime = async (source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT) => {
    const path = `${DATA_VALIDATION_FAILS_OVER_TIME_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationFailsOverTimePoint[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationFailsOverTimePoint[]) : [];
  };

  fetchDataValidationTopFailingDeals = async (source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT) => {
    const path = `${DATA_VALIDATION_TOP_FAILING_DEALS_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationTopFailingDealPoint[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationTopFailingDealPoint[]) : [];
  };

  fetchDataValidationFacilityFailures = async (
    source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT,
  ) => {
    const path = `${DATA_VALIDATION_FACILITY_FAILURES_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationFacilityFailuresPoint[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationFacilityFailuresPoint[]) : [];
  };

  fetchDataValidationRuleFailureBreakdown = async (
    source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT,
  ) => {
    const path = `${DATA_VALIDATION_RULE_FAILURE_BREAKDOWN_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationRuleFailureBreakdownRow[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationRuleFailureBreakdownRow[]) : [];
  };

  fetchDataValidationActiveFailingDeals = async (
    source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT,
  ) => {
    const path = `${DATA_VALIDATION_ACTIVE_FAILING_DEALS_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationActiveFailingDealsPoint[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationActiveFailingDealsPoint[]) : [];
  };

  fetchDataValidationFailsByDate = async (source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT) => {
    const path = `${DATA_VALIDATION_FAILS_BY_DATE_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? (parsed as DataValidationFailsByDateRow[]) : [];
    }
    return Array.isArray(raw) ? (raw as DataValidationFailsByDateRow[]) : [];
  };

  fetchLoanLevelFailuresAsOfDates = async () => {
    const raw = await this.get<unknown>({ path: LOAN_LEVEL_FAILURES_AS_OF_DATES_PATH } as never);
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return [];
      }
      const parsed = JSON.parse(text) as unknown;
      return Array.isArray(parsed) ? parsed.map(String) : [];
    }
    return Array.isArray(raw) ? raw.map(String) : [];
  };

  fetchDataValidationSummaryKpi = async (source: DataValidationKpiSource = DATA_VALIDATION_KPI_SOURCE_DEFAULT) => {
    const path = `${DATA_VALIDATION_SUMMARY_KPI_PATH}?source=${encodeURIComponent(source)}`;
    const raw = await this.get<unknown>({ path } as never);
    return parseJsonResponse<DataValidationSummaryKpi>(raw);
  };
}
